import { injectable } from 'inversify';

import { FormAttribute, NodeAction } from '@app/gen/events';

import { Validator } from '../types';
import { AVAILABLE_VALIDATOR_TYPES, VACATION_END_ATTR_ID } from '../constants';

@injectable()
export class EventsFormAttributesMapper {
  formatValidators(
    validators: Validator[],
    formAttributes: FormAttribute[] | undefined,
  ) {
    return validators.map((validator) => {
      if (validator.type === 'values_compare') {
        return {
          ...validator,
          from_attribute_id: validator.left_attribute_id,
          to_attribute_id: validator.right_attribute_id,
          from_attribute_name: formAttributes?.find(
            (attr) => attr.id === validator.left_attribute_id,
          )?.name,
          to_attribute_name: formAttributes?.find(
            (attr) => attr.id === validator.right_attribute_id,
          )?.name,
        };
      }

      if (validator.type === 'available_days_check') {
        return {
          ...validator,
          from_attribute_id: validator.days_attribute_id,
          to_attribute_id: validator.vacation_type_attribute_id,
          from_attribute_name: formAttributes?.find(
            (attr) => attr.id === validator.days_attribute_id,
          )?.name,
          to_attribute_name: formAttributes?.find(
            (attr) => attr.id === validator.vacation_type_attribute_id,
          )?.name,
        };
      }

      return validator;
    });
  }

  processAttributeValidators(
    action: NodeAction,
    attribute: FormAttribute,
    validators: Validator[],
  ) {
    return validators.filter(
      (validator) =>
        ('attribute_id' in validator &&
          validator.attribute_id === attribute.id) ||
        ('from_attribute_id' in validator &&
          validator.from_attribute_id === attribute.id) ||
        ('to_attribute_id' in validator &&
          validator.to_attribute_id === attribute.id) ||
        ('length_attribute_id' in validator &&
          validator.length_attribute_id === attribute.id) ||
        (validator.type === 'compare_date_collection' &&
          attribute.id ===
            action.form_attributes.find((attr) => {
              const expressionAliases = attr.options?.expression_aliases;
              if (
                expressionAliases &&
                '$newstart' in expressionAliases &&
                '$newend' in expressionAliases
              ) {
                return (
                  expressionAliases.$newstart === validator.from_attribute_id &&
                  expressionAliases.$newend === validator.to_attribute_id
                );
              }
            })?.id),
    );
  }

  processIsValidatorsListShouldBeShown(
    attribute: FormAttribute,
    validators: Validator[],
  ) {
    return validators.some((validator) => {
      if (!AVAILABLE_VALIDATOR_TYPES.includes(validator.type || '')) {
        return false;
      }

      if (
        attribute.type === 'text_fixed_catalog' ||
        attribute.type === 'datetime'
      ) {
        return !(
          validator.type === 'non_working_days_included_check' ||
          validator.type === 'exceeding_limit'
        );
      }

      if (attribute.type === 'choice') {
        const choices = attribute.options?.choices || [];

        const hasNumericChoices =
          choices.length > 0 && choices.every((c) => !isNaN(Number(c.value)));

        const isUnsupportedCompareOperation =
          (validator.type === 'values_compare' ||
            validator.type === 'check_value') &&
          !hasNumericChoices &&
          validator.operation !== 'eq' &&
          validator.operation !== 'ne';

        const isValuesCompareForOtherAttribute =
          validator.type === 'values_compare' &&
          'to_attribute_id' in validator &&
          validator.to_attribute_id !== attribute.id;

        const isAvailableDaysCheckWithoutName =
          validator.type === 'available_days_check' &&
          !(
            'from_attribute_name' in validator && validator.from_attribute_name
          );

        return !(
          validator.type === 'non_working_days_included_check' ||
          validator.type === 'exceeding_limit' ||
          isUnsupportedCompareOperation ||
          isValuesCompareForOtherAttribute ||
          isAvailableDaysCheckWithoutName
        );
      }

      if (attribute.type === 'date') {
        return !(
          validator.type === 'non_working_days_included_check' ||
          validator.type === 'exceeding_limit' ||
          (validator.type === 'absence_days_check' && !validator.days_check)
        );
      }

      if (attribute.type === 'text_catalog') {
        if (
          validator.type === 'values_compare' ||
          validator.type === 'check_value'
        ) {
          return validator.operation === 'eq' || validator.operation === 'ne';
        }

        return !(
          validator.type === 'non_working_days_included_check' ||
          validator.type === 'exceeding_limit'
        );
      }

      const numberValidator = validators.find((item) => item.type === 'number');

      const luhnValidator = !attribute.options?.expression
        ? validators.find((item) => item.type === 'luhn_algorithm')
        : undefined;

      if (
        validator.type === 'values_compare' ||
        validator.type === 'check_value'
      ) {
        return (
          numberValidator ||
          validator.operation === 'eq' ||
          validator.operation === 'ne'
        );
      }

      return !(
        validator.type === 'non_working_days_included_check' ||
        validator.type === 'exceeding_limit' ||
        luhnValidator ||
        numberValidator
      );
    });
  }

  process(action: NodeAction, validators: Validator[], companyName: string) {
    const formattedValidators = this.formatValidators(
      validators,
      action.form_attributes,
    );

    return action.form_attributes.map((attribute) => {
      const rangeValidators = formattedValidators.filter(
        (validator) =>
          ('from_attribute_id' in validator &&
            validator.from_attribute_id === attribute.id) ||
          ('to_attribute_id' in validator &&
            validator.to_attribute_id === attribute.id &&
            validator.type !== 'available_days_check') ||
          ('length_attribute_id' in validator &&
            validator.length_attribute_id === attribute.id),
      );

      const relatedAttribute =
        rangeValidators
          .map((validator) => {
            if (
              'from_attribute_id' in validator &&
              validator.from_attribute_id === attribute.id
            ) {
              return validator.to_attribute_id;
            }
            if (
              'to_attribute_id' in validator &&
              validator.to_attribute_id === attribute.id
            ) {
              return validator.from_attribute_id;
            }

            if (validator.type === 'compare_date_collection') {
              const findAttribute = action.form_attributes.find((attr) => {
                const expressionAliases = attr.options?.expression_aliases;
                if (
                  expressionAliases &&
                  '$newstart' in expressionAliases &&
                  '$newend' in expressionAliases
                ) {
                  return (
                    expressionAliases.$newstart ===
                      validator.from_attribute_id &&
                    expressionAliases.$newend === validator.to_attribute_id
                  );
                }
              });

              return findAttribute?.id;
            }
          })
          .find((value) => value) || '';

      const attributeType =
        rangeValidators
          .map((validator) => {
            if (
              'from_attribute_id' in validator &&
              validator.from_attribute_id === attribute.id
            ) {
              return 'rangeStart';
            }
            if (
              'to_attribute_id' in validator &&
              validator.to_attribute_id === attribute.id
            ) {
              return 'rangeEnd';
            }

            return 'single';
          })
          .find((value) => value) || 'single';

      const startValidator = formattedValidators.find(
        (validator) =>
          'from_attribute_id' in validator &&
          validator.from_attribute_id === attribute.id,
      );

      const endValidator = formattedValidators.find(
        (validator) =>
          'to_attribute_id' in validator &&
          validator.to_attribute_id === attribute.id &&
          validator.type !== 'available_days_check',
      );

      const processedValidators = this.processAttributeValidators(
        action,
        attribute,
        formattedValidators,
      );

      return {
        ...attribute,
        relatedAttribute,
        companyName,
        attributeType,
        nameValidating: action.form_attributes.find((attr) => {
          if (endValidator) {
            return (
              'from_attribute_id' in endValidator &&
              attr.id === endValidator.from_attribute_id
            );
          } else if (startValidator) {
            return (
              'to_attribute_id' in startValidator &&
              attr.id === startValidator.to_attribute_id
            );
          } else {
            return attr.id === VACATION_END_ATTR_ID;
          }
        })?.name,
        validators: processedValidators,
        isValidatorsListShouldBeShown:
          this.processIsValidatorsListShouldBeShown(
            attribute,
            processedValidators,
          ),
      };
    });
  }
}
